import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Switch,
    TextField,
    Typography
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { getTagOptions } from '../../api/admin';
import { MIXED_BANNER_TYPES, MixedBanner, Tag } from '../../api/models';

/**
 * TASK-52 — Create / Edit banner dialog.
 *
 * Campaign banners (`type=tag`) carry N hashtags, which DevExtreme inline add
 * cannot express: it only renders single-value editors. So tag campaigns are
 * created and edited here, and the grid keeps handling the simple types.
 *
 * The Type dropdown is the only place a banner type is decided. Nothing infers
 * it, and ops never have to type the literal string `tag`.
 *
 * Test ids sit on the outer Material-UI components; the inner <input> is reached
 * with `getByTestId(...).locator('input')` (the TS 3.4 React typings reject
 * `data-*` inside the `inputProps` object literal).
 */
export type BannerFormValues = {
    type: string,
    imageId: string,
    published: boolean,
    priority: string,
    validFrom: string,
    validUntil: string,
    tagIds: number[],
    spotId: string,
    prizeId: string,
    couponId: string,
    url: string
}

const emptyValues = (): BannerFormValues => ({
    type: 'tag',
    imageId: '',
    published: false,
    priority: '',
    validFrom: '',
    validUntil: '',
    tagIds: [],
    spotId: '',
    prizeId: '',
    couponId: '',
    url: ''
})

const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`)

/**
 * `<input type="datetime-local">` speaks `YYYY-MM-DDTHH:mm` in *local* time.
 * The backend column is a naive DateTime, so the raw value is posted as-is
 * (no `Z`) and local components are used to render an existing value back —
 * that round-trips a naive value without shifting it.
 */
export const toDatetimeLocal = (value?: string | null): string => {
    if (value == null || value === '') {
        return ''
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return `${value}T00:00`
    }
    const date = new Date(value)
    if (isNaN(date.getTime())) {
        return ''
    }
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const mixedBannerToFormValues = (banner?: MixedBanner | null): BannerFormValues => {
    if (banner == null) {
        return emptyValues()
    }
    // `tags` is the new field; fall back to the legacy single `tag` so editing
    // a banner created before TASK-52 keeps its hashtag.
    const bannerTags: Tag[] = banner.tags != null && banner.tags.length > 0
        ? banner.tags
        : (banner.tag != null ? [banner.tag] : [])
    return {
        type: banner.type != null ? banner.type : 'tag',
        imageId: banner.imageId != null
            ? String(banner.imageId)
            : (banner.image != null ? String(banner.image.id) : ''),
        published: banner.published === true,
        priority: banner.priority != null ? String(banner.priority) : '',
        validFrom: toDatetimeLocal(banner.validFrom),
        validUntil: toDatetimeLocal(banner.validUntil),
        tagIds: bannerTags.map(tag => tag.id),
        spotId: banner.spotId != null
            ? String(banner.spotId)
            : (banner.spot != null ? String(banner.spot.id) : ''),
        prizeId: banner.prizeId != null
            ? String(banner.prizeId)
            : (banner.prize != null ? String(banner.prize.id) : ''),
        couponId: banner.couponId != null
            ? String(banner.couponId)
            : (banner.coupon != null ? String(banner.coupon.id) : ''),
        url: banner.url != null ? banner.url : ''
    }
}

export const validateBannerValues = (values: BannerFormValues): string | null => {
    if (MIXED_BANNER_TYPES.indexOf(values.type) === -1) {
        return 'Choose a banner type'
    }
    if (values.type === 'tag' && values.tagIds.length === 0) {
        return 'Pick at least one hashtag for a tag campaign'
    }
    if (values.type === 'spot' && values.spotId === '') {
        return 'Spot id is required for a spot banner'
    }
    if (values.type === 'prize' && values.prizeId === '') {
        return 'Prize id is required for a prize banner'
    }
    if (values.type === 'coupon' && values.couponId === '') {
        return 'Coupon id is required for a coupon banner'
    }
    if (values.type === 'url' && values.url === '') {
        return 'Url is required for a url banner'
    }
    if (values.validFrom !== '' && values.validUntil !== '' && values.validUntil < values.validFrom) {
        return '"Valid until" must be after "Valid from"'
    }
    return null
}

/**
 * `createMixedBanner` / `patchMixedBanner` POST/PATCH the whole args object, so
 * the dialog hands them the exact payload the backend schema expects.
 */
export const toBannerArgs = (values: BannerFormValues): any => {
    const args: any = {
        type: values.type,
        published: values.published,
        // null clears the window; the backend fields are allow_none.
        validFrom: values.validFrom === '' ? null : values.validFrom,
        validUntil: values.validUntil === '' ? null : values.validUntil
    }
    if (values.imageId !== '') {
        args.imageId = Number(values.imageId)
    }
    if (values.priority !== '') {
        args.priority = Number(values.priority)
    }
    if (values.type === 'tag') {
        args.tagIds = values.tagIds
    }
    if (values.type === 'spot') {
        args.spotId = Number(values.spotId)
    }
    if (values.type === 'prize') {
        args.prizeId = Number(values.prizeId)
    }
    if (values.type === 'coupon') {
        args.couponId = Number(values.couponId)
    }
    if (values.type === 'url') {
        args.url = values.url
    }
    return args
}

type Props = {
    initial?: MixedBanner | null,
    onClose: () => void,
    onSubmit: (args: any) => void
}

const CampaignBannerDialog = (props: Props) => {
    const { initial, onClose, onSubmit } = props;

    const [values, setValues] = useState<BannerFormValues>(() => mixedBannerToFormValues(initial));
    const [tags, setTags] = useState<Tag[]>([]);
    const [tagSearch, setTagSearch] = useState('');
    const [tagsLoading, setTagsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        getTagOptions().then(options => {
            if (cancelled) {
                return;
            }
            setTags(options);
            setTagsLoading(false);
        }).catch(() => {
            if (cancelled) {
                return;
            }
            setTags([]);
            setTagsLoading(false);
            setError('Could not load tags from /admin/paginated-tag');
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const setField = (name: keyof BannerFormValues, value: any) => {
        setValues(previous => {
            const next = { ...previous };
            (next as any)[name] = value;
            return next;
        });
    }

    const onTypeChange = (type: string) => {
        setError(null);
        setField('type', type);
    }

    const handleSubmit = () => {
        const validationError = validateBannerValues(values);
        if (validationError != null) {
            setError(validationError);
            return;
        }
        onSubmit(toBannerArgs(values));
    }

    const normalizedSearch = tagSearch.trim().replace(/^#/, '').toLowerCase();
    const filteredTags = normalizedSearch === ''
        ? tags
        : tags.filter(tag => String(tag.value).toLowerCase().indexOf(normalizedSearch) !== -1);

    const renderSelectedTags = (selected: any) => {
        const selectedIds = (selected != null ? selected : []) as number[];
        if (selectedIds.length === 0) {
            return <Typography color='textSecondary'>Pick one or more hashtags</Typography>;
        }
        return (
            <span>
                {selectedIds.map(id => {
                    const tag = tags.filter(candidate => candidate.id === id)[0];
                    return (
                        <Chip
                            key={id}
                            label={`#${tag != null ? tag.value : id}`}
                            style={{ margin: '2px' }}
                        />
                    );
                })}
            </span>
        );
    }

    return (
        <Dialog
            open
            fullWidth
            maxWidth='sm'
            onClose={onClose}
        >
            <DialogTitle>
                {initial != null ? 'Edit banner' : 'Create campaign banner'}
            </DialogTitle>
            <DialogContent data-testid='campaign-banner-dialog'>
                <TextField
                    select
                    fullWidth
                    margin='dense'
                    label='Type'
                    data-testid='campaign-type'
                    value={values.type}
                    onChange={event => onTypeChange(event.target.value)}
                >
                    {MIXED_BANNER_TYPES.map(type => (
                        <MenuItem key={type} value={type} data-testid={`campaign-type-option-${type}`}>
                            {type}
                        </MenuItem>
                    ))}
                </TextField>

                {values.type === 'tag' ?
                    <div>
                        <TextField
                            fullWidth
                            margin='dense'
                            label='Search hashtags'
                            data-testid='campaign-tag-search'
                            value={tagSearch}
                            onChange={event => setTagSearch(event.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            margin='dense'
                            label='Hashtags'
                            data-testid='campaign-tag-select'
                            value={values.tagIds}
                            onChange={event => setField('tagIds', event.target.value)}
                            SelectProps={{
                                multiple: true,
                                renderValue: renderSelectedTags,
                                MenuProps: { PaperProps: { style: { maxHeight: '40vh' } } }
                            }}
                        >
                            {filteredTags.map(tag => (
                                <MenuItem
                                    key={tag.id}
                                    value={tag.id}
                                    data-testid={`campaign-tag-option-${tag.id}`}
                                >
                                    {`#${tag.value}`}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant='caption' color='textSecondary' data-testid='campaign-tag-count'>
                            {tagsLoading
                                ? 'Loading hashtags…'
                                : `${filteredTags.length} of ${tags.length} hashtags`}
                        </Typography>
                    </div>
                    : null}

                {values.type === 'spot' ?
                    <TextField
                        fullWidth
                        margin='dense'
                        type='number'
                        label='Spot id'
                        data-testid='campaign-spot-id'
                        value={values.spotId}
                        onChange={event => setField('spotId', event.target.value)}
                    /> : null}

                {values.type === 'prize' ?
                    <TextField
                        fullWidth
                        margin='dense'
                        type='number'
                        label='Prize id'
                        data-testid='campaign-prize-id'
                        value={values.prizeId}
                        onChange={event => setField('prizeId', event.target.value)}
                    /> : null}

                {values.type === 'coupon' ?
                    <TextField
                        fullWidth
                        margin='dense'
                        type='number'
                        label='Coupon id'
                        data-testid='campaign-coupon-id'
                        value={values.couponId}
                        onChange={event => setField('couponId', event.target.value)}
                    /> : null}

                {values.type === 'url' ?
                    <TextField
                        fullWidth
                        margin='dense'
                        label='Url'
                        data-testid='campaign-url'
                        value={values.url}
                        onChange={event => setField('url', event.target.value)}
                    /> : null}

                <TextField
                    fullWidth
                    margin='dense'
                    type='number'
                    label='Image id (hero)'
                    data-testid='campaign-image-id'
                    value={values.imageId}
                    onChange={event => setField('imageId', event.target.value)}
                />

                <TextField
                    fullWidth
                    margin='dense'
                    type='number'
                    label='Priority'
                    data-testid='campaign-priority'
                    value={values.priority}
                    onChange={event => setField('priority', event.target.value)}
                />

                <TextField
                    fullWidth
                    margin='dense'
                    type='datetime-local'
                    label='Valid from (optional)'
                    data-testid='campaign-valid-from'
                    InputLabelProps={{ shrink: true }}
                    value={values.validFrom}
                    onChange={event => setField('validFrom', event.target.value)}
                />

                <TextField
                    fullWidth
                    margin='dense'
                    type='datetime-local'
                    label='Valid until (optional)'
                    data-testid='campaign-valid-until'
                    InputLabelProps={{ shrink: true }}
                    value={values.validUntil}
                    onChange={event => setField('validUntil', event.target.value)}
                />

                <FormControl margin='dense'>
                    <FormControlLabel
                        control={
                            <Switch
                                color='primary'
                                checked={values.published}
                                data-testid='campaign-published'
                                onChange={event => setField('published', event.target.checked)}
                            />
                        }
                        label='Published'
                    />
                    <FormHelperText>
                        Unpublish to retire a live campaign in one click.
                    </FormHelperText>
                </FormControl>

                {error != null ?
                    <Typography color='error' data-testid='campaign-error'>
                        {error}
                    </Typography> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-testid='campaign-cancel'>
                    Cancel
                </Button>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSubmit}
                    data-testid='campaign-submit'
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CampaignBannerDialog;
